import { useEffect, useRef } from 'react';
import axios from 'axios';

interface Props {
  audioUrl: string;
  className?: string;
  style?: React.CSSProperties;
  width: number;
  height?: number;
  backgroundColor?: string;
  foregroundColor?: string;
  waveColor?: string;
}

declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
  }
}

function WaveView({
  audioUrl,
  className,
  style,
  width,
  height = 80,
  backgroundColor = 'transparent',
  foregroundColor = '#286EFA',
  waveColor = 'white',
}: Props) {
  const ref = useRef(null);

  useEffect(() => {
    drawLoadingText();
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    axios.get(audioUrl, { responseType: 'arraybuffer' }).then((res) => {
      audioCtx
        .decodeAudioData(res.data)
        .then((buffer) => {
          drawWave(buffer);
        })
        .catch(() => {
          drawBlackBg();
        });
    });
  }, [audioUrl]);

  const drawLoadingText = () => {
    const element = ref.current;
    if (element) {
      const canvas = element as HTMLCanvasElement;
      const ctx = (canvas as HTMLCanvasElement).getContext('2d');
      if (ctx) {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, width, height);
        roundRect(ctx, 0, 0, width, height, 5, foregroundColor);
        ctx.fillStyle = waveColor;
        ctx.font = '12px sans-serif ';
        ctx.fillText('音频加载中...', 10, 6 + height / 2, width);
      }
    }
  };

  const drawBlackBg = () => {
    const element = ref.current;
    if (element) {
      const canvas = element as HTMLCanvasElement;
      const ctx = (canvas as HTMLCanvasElement).getContext('2d');
      if (ctx) {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, width, height);
        roundRect(ctx, 0, 0, width, height, 5, foregroundColor);
        const y = height / 2;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.beginPath();
        ctx.moveTo(5, y);
        ctx.lineTo(width - 5, y);
        ctx.stroke();
      }
    }
  };

  const drawWave = (buffer: AudioBuffer) => {
    const originData: Float32Array = buffer.getChannelData(0);
    const data: number[] = [];
    const positives = [];
    const negatives = [];
    const step = Math.round(originData.length / width / 10);

    for (let i = 0; i < originData.length; i += step) {
      data.push(originData[i]);
    }
    for (let j = 0, len = data.length / 10; j < len; j++) {
      const temp = data.slice(j * 10, (j + 1) * 10);
      positives.push(Math.max.apply(null, temp));
      negatives.push(Math.min.apply(null, temp));
    }
    const element = ref.current;
    if (element) {
      const canvas = element as HTMLCanvasElement;
      const ctx = (canvas as HTMLCanvasElement).getContext('2d');
      if (ctx) {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, width, height);
        roundRect(ctx, 0, 0, width, height, 5, foregroundColor);
        const x = 0;
        const y = height / 2;
        ctx.fillStyle = waveColor;
        ctx.beginPath();
        ctx.moveTo(x, y);
        for (let k = 0; k < positives.length; k++) {
          ctx.lineTo(x + k + 0.5, y - (height / 2) * positives[k]);
        }
        for (let l = negatives.length - 1; l >= 0; l--) {
          ctx.lineTo(x + l + 0.5, y + (height / 2) * Math.abs(negatives[l]));
        }
        ctx.fill();
      }
    }
  };

  function roundRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    radius: number,
    color: string
  ) {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
    ctx.lineTo(x + w, y + h - radius);
    ctx.quadraticCurveTo(x + w, y + h, x + width - radius, y + h);
    ctx.lineTo(x + radius, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
    ctx.fill();
  }

  return (
    <canvas
      ref={ref}
      width={width}
      height={height}
      className={className}
      style={style}
    />
  );
}

export default WaveView;
